<template>
  <Card :id="result.shipmentId" class="bulk-tender-result-card">
    <template #content>
      <div class="top-row flex justify-content-between align-items-center">
        <div class="top-row-info flex flex-gap-1">
          <h3
            :id="`${result.shipmentId}-header`"
            class="bulk-tender-result-card-header"
          >
            <router-link
              :to="{
                name: 'shipmentDetail',
                params: { shipmentId: result.shipmentId },
              }"
            >
              {{
                t('shipments.shipmentNumParam', {
                  param: `${result.shipmentId}`,
                })
              }}
            </router-link>
          </h3>
          <ShipmentStatusChip v-if="!hasErrors" :status="result.status" />
          <RygenChip
            v-else
            :label="t('shipments.failedToTender')"
            :severity="Severity.DANGER"
          />
        </div>
      </div>
      <div v-if="!hasErrors">
        <div class="additional-row">
          {{ carrierNameAndScac }}
        </div>

        <div
          class="additional-row flex flex-gap-1 align-items-center text-gray-darkest"
        >
          <div>
            {{ result.originName }}
          </div>
          <FontAwesomeIcon
            aria-hidden="true"
            class="text-gray-darker"
            :icon="['fal', 'long-arrow-right']"
          />
          <div>
            {{ result.destinationName }}
          </div>
        </div>
        <div class="additional-row">
          {{ tenderMethod }}
        </div>
        <div
          v-if="result.request?.tenderMethod === TenderMethod.EMAIL"
          class="additional-row"
        >
          {{ emails }}
        </div>
      </div>
      <div v-else>
        <div class="additional-row">
          {{ errorMessage }}
        </div>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { translateEnum } from '@rygen/rygen-ui/i18n';
import { Severity, TenderMethod } from '@rygen/rygen-ui/model';

import { BulkTenderResult } from '@/model';

import ShipmentStatusChip from '@/components/shipment/ShipmentStatusChip.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import Card from 'primevue/card';

const props = defineProps({
  result: {
    type: BulkTenderResult,
    default: () => new BulkTenderResult(),
  },
});

const { result } = toRefs(props);

const { t } = useI18n();

const carrierNameAndScac = computed((): string => {
  return `${result.value.request?.carrier?.name} (${result.value.request?.carrier?.scac})`;
});

const tenderMethod = computed((): string => {
  return (
    `${t('shipments.tenderMethods')}: ` +
    translateEnum(result.value.request?.tenderMethod, 'TenderMethod')
  );
});

const emails = computed((): string => {
  return `${t('common.email')}: ${result.value.request?.tenderConfigOptions?.emails}`;
});

const hasErrors = computed((): boolean => {
  return result.value.errors !== undefined;
});

const errorMessage = computed((): string => {
  return `${t('shipments.errors.tender')}: ${result.value.errors}`;
});
</script>

<style>
.bulk-tender-result-card.p-card {
  border: 1px solid var(--p-gray-200);

  .p-card-body {
    padding: 0;

    .top-row {
      .top-row-info {
        padding: var(--sp-3);
        margin-right: auto;

        .bulk-tender-result-card-header {
          color: var(--p-gray-600);
          font-size: 14px;
          font-weight: 700;
        }
      }
    }

    .additional-row {
      padding: var(--sp-3);
      font-size: 13px;
      font-weight: 400;

      & + .additional-row {
        padding-top: 0;
      }
    }
  }
}
</style>
